import * as React from 'react';

import { Button, TextField, Select } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useSetAtom } from 'jotai';
import { useLocation } from 'wouter';

import { completeSignUpMutationAtom } from '../../sign-up/hooks/mutations/completeSignUpMutation';
import { $signUp } from '../../sign-up/state/signUp.state';

import { useTranslations } from '~/shared/i18n/useTranslations';

const TIMEZONES = [
  { key: 'America/New_York', label: 'Eastern Time (ET)' },
  { key: 'America/Chicago', label: 'Central Time (CT)' },
  { key: 'America/Denver', label: 'Mountain Time (MT)' },
  { key: 'America/Phoenix', label: 'Mountain Time - Arizona (MT)' },
  { key: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { key: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { key: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
];

export const VerifyEmailForm = () => {
  const t = useTranslations();
  const toast = useToast();
  const [, setLocation] = useLocation();

  // Get token from URL
  const token = React.useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('token');
  }, []);

  const [signUpState, setSignUpState] = useAtom($signUp);
  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [organizationName, setOrganizationName] = React.useState('');
  const [timezone, setTimezone] = React.useState('America/New_York');
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');

  // Create mutation atom
  const $completeSignUpMutation = React.useMemo(
    () => completeSignUpMutationAtom(),
    [],
  );

  const completeSignUp = useSetAtom($completeSignUpMutation);
  const [completeSignUpMutationState] = useAtom($completeSignUpMutation);

  const isPending = completeSignUpMutationState.isPending;

  // Redirect if no token
  React.useEffect(() => {
    if (!token) {
      toast.error(t.auth.verifyEmail.invalidLink);
      setLocation('/sign-up');
    }
  }, [token, setLocation, toast, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token) {
      toast.error(t.auth.verifyEmail.invalidLink);
      return;
    }

    if (password !== confirmPassword) {
      toast.error(t.auth.verifyEmail.passwordsDoNotMatch);
      return;
    }

    if (password.length < 8) {
      toast.error(t.auth.verifyEmail.passwordTooShort);
      return;
    }

    try {
      const completeSignUpResult = await completeSignUp({
        token,
        firstName,
        lastName,
        organizationName,
        timezone,
        password,
      });

      if (
        completeSignUpResult.authenticationCompleteSignUp.__typename ===
        'SignUpCompletedResult'
      ) {
        setSignUpState({
          ...signUpState,
          step: 'success',
          email: completeSignUpResult.authenticationCompleteSignUp.email,
          organizationId:
            completeSignUpResult.authenticationCompleteSignUp.organizationId,
          userId: completeSignUpResult.authenticationCompleteSignUp.userId,
          completed: true,
        });
        // Navigate to success page
        setLocation('/sign-up');
        return;
      }

      if (
        completeSignUpResult.authenticationCompleteSignUp.__typename ===
        'InvalidSignUpInvitationError'
      ) {
        const reason = completeSignUpResult.authenticationCompleteSignUp.reason;
        // Provide user-friendly message for password errors
        if (reason.toLowerCase().includes('password')) {
          toast.error(t.auth.verifyEmail.passwordRequirements);
        } else {
          toast.error(t.auth.verifyEmail.verificationFailed(reason));
        }
        return;
      }

      toast.error(t.auth.verifyEmail.unexpectedError);
    } catch (err) {
      toast.error(String(err));
    }
  };

  if (!token) {
    return null;
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="@VerifyEmailForm flex flex-col gap-5"
    >
      <div className="mb-2">
        <h1 className="text-2xl font-semibold text-foreground">
          {t.auth.verifyEmail.title}
        </h1>
        <p className="mt-1 text-sm text-neutral">
          {t.auth.verifyEmail.subtitle}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <TextField
          autoFocus
          label={t.auth.verifyEmail.firstName}
          placeholder="John"
          type="text"
          onChange={setFirstName}
          value={firstName}
          isRequired
        />

        <TextField
          label={t.auth.verifyEmail.lastName}
          placeholder="Doe"
          type="text"
          onChange={setLastName}
          value={lastName}
          isRequired
        />
      </div>

      <TextField
        label={t.auth.verifyEmail.organizationName}
        placeholder="Acme Inc"
        type="text"
        onChange={setOrganizationName}
        value={organizationName}
        isRequired
      />

      <Select<string>
        label={t.auth.verifyEmail.timezone}
        options={TIMEZONES}
        value={timezone}
        onChange={(value) => value && setTimezone(value)}
        isRequired
      />

      <TextField
        label={t.auth.verifyEmail.password}
        placeholder={t.auth.verifyEmail.passwordPlaceholder}
        type="password"
        onChange={setPassword}
        value={password}
        isRequired
      />

      <TextField
        label={t.auth.verifyEmail.confirmPassword}
        placeholder={t.auth.verifyEmail.confirmPasswordPlaceholder}
        type="password"
        onChange={setConfirmPassword}
        value={confirmPassword}
        isRequired
        className="mb-4"
      />

      <Button
        type="submit"
        variant="solid"
        color="primary"
        isLoading={isPending}
      >
        {t.auth.verifyEmail.completeSetup}
      </Button>

      <div className="text-center text-sm text-neutral">
        {t.auth.verifyEmail.alreadyHaveAccount}{' '}
        <a
          href="/sign-in"
          className="text-primary-darker hover:text-primary/80"
        >
          {t.auth.verifyEmail.signIn}
        </a>
      </div>
    </form>
  );
};
